import styled from "styled-components";
import ReactFullpage from "@fullpage/react-fullpage";
import { HomeHeroSection } from "@/components/ui/Screens/Homepage/HomeHeroSection";
import { HomeUtilities } from "@/components/ui/Screens/Homepage/HomeUtilities";
import { HomeContact } from "@/components/ui/Screens/Homepage/HomeContact";
import { HomeFleets } from "@/components/ui/Screens/Homepage/HomeFleets";
import { HomeEachService, ServicesIntroduction } from "@/components/ui/Screens/Homepage/HomeServices";
import { HomeIntro } from "@/components/ui/Screens/Homepage/HomeIntro";
import { MainFooter } from "@/components/ui/Footer/MainFooter";
import { useEffect, useRef, useState } from "react";
import { RAButtonBackToTop } from "@/components/customs";
import { useHeaderSolid } from "@/hooks/HeaderSolidContext";

const FULL_PAGE_API_KEY = process.env.NEXT_PUBLIC_FULL_PAGE_API_KEY;

export const SERVICES_STEP = {
	FIRST: "FIRST",
	SECOND: "SECOND",
	THIRD: "THIRD",
};

export function ComponentHomepageSnap({ pageContent, ...props }) {
	const { content = {} } = pageContent;
	const [activeSlide, setActiveSlide] = useState(0);
	const servicesRef = useRef(null);

	const { headerSolid } = useHeaderSolid();

	useEffect(() => {
		return () => {
			fullpage_api.silentMoveTo(1);
		};
	}, []);

	const onLeave = (origin, destination, direction) => {
		setActiveSlide(destination.index);
		headerSolid(destination.index);
	};

	return (
		<>
			<div
				ref={servicesRef}
				id="fixed-introduction"
				style={{
					position: "absolute",
					top: "80px",
					opacity: activeSlide >= 3 && activeSlide <= 5 ? 1 : 0,
					zIndex: activeSlide >= 3 && activeSlide <= 5 ? 100 : "auto",
					transition: "all 0.6s linear",
					background: "#fff",
					height: "16vh",
					display: "flex",
					alignItems: "flex-end",
				}}
			>
				<ServicesIntroduction explore_our_innovative_solutions={content?.explore_our_innovative_solutions} />
			</div>

			<ReactFullpage
				licenseKey={FULL_PAGE_API_KEY}
				bigSectionsDestination={"top"}
				onLeave={onLeave}
				scrollingSpeed={1100}
				fadingEffect={true}
				lazyLoading={false}
				normalScrollElements=".ant-picker-panel-container, .ant-modal, .ant-picker-time-panel-column, .mapboxgl-canvas, .ant-modal-wrap, .ra-search-airport-ant-select-dropdown"
				credits={{ enabled: false }}
				render={() => {
					return (
						<ReactFullpage.Wrapper>
							<div className="section" id="hero-section">
								<HomeHeroSection banner={content?.banner} />
							</div>
							<EachSlide className="section" id="home-utilities-section">
								<BodyContainer>
									<HomeUtilities why_fly_private={content?.why_fly_private} />
								</BodyContainer>
							</EachSlide>
							<EachSlide className="section" id="home-fleets-section">
								<Overlay>
									<HomeFleetsBodyContainer>
										<BodyContainer>
											<HomeFleets step_aboard_our_fleet={content?.step_aboard_our_fleet} />
										</BodyContainer>
									</HomeFleetsBodyContainer>
								</Overlay>
							</EachSlide>
							<EachSlide
								className="section"
								id="home-first-service-section"
								style={{ justifyContent: "flex-center" }}
							>
								<BodyContainer
									style={{
										transition: "all 0.6s linear",
										paddingTop: servicesRef?.current?.clientHeight || "50px",
									}}
								>
									<HomeEachService
										explore_our_innovative_solutions={content?.explore_our_innovative_solutions}
										step={"FIRST"}
									/>
								</BodyContainer>
							</EachSlide>
							<EachSlide
								className="section"
								id="home-second-service-section"
								style={{ justifyContent: "flex-center" }}
							>
								<BodyContainer
									style={{
										transition: "all 0.6s linear",
										paddingTop: servicesRef?.current?.clientHeight || "50px",
									}}
								>
									<HomeEachService
										explore_our_innovative_solutions={content?.explore_our_innovative_solutions}
										step={"SECOND"}
									/>
								</BodyContainer>
							</EachSlide>
							<EachSlide
								className="section"
								id="home-third-service-section"
								style={{ justifyContent: "flex-center" }}
							>
								<BodyContainer
									style={{
										transition: "all 0.6s linear",
										paddingTop: servicesRef?.current?.clientHeight || "50px",
									}}
								>
									<HomeEachService
										explore_our_innovative_solutions={content?.explore_our_innovative_solutions}
										step={"THIRD"}
									/>
								</BodyContainer>
							</EachSlide>
							<EachSlide className="section" id="home-intro-section">
								<HomeIntro video={content?.video} snap active={activeSlide === 6} />
							</EachSlide>
							<EachSlide className="section fp-auto-height" id="home-contact-section">
								<BodyContainer
									style={{
										paddingTop: 100,
										paddingBottom: 100,
										width: "100vw",
									}}
								>
									<HomeContact connect_with_us={content?.connect_with_us} />
								</BodyContainer>
							</EachSlide>
							<EachSlide className="section fp-auto-height" id="footer-section">
								<footer
									style={{
										width: "100%",
										scrollSnapAlign: "end",
									}}
								>
									<MainFooter />
									<RAButtonBackToTop onClick={() => fullpage_api.moveTo(1)} />
								</footer>
							</EachSlide>
						</ReactFullpage.Wrapper>
					);
				}}
			/>
		</>
	);
}
const BodyContainer = styled.div`
	max-width: 1442px;
	padding: 0 136px;
	width: 100%;
	&.home-utilities-container-outer {
		margin-top: 50px;
	}
	@media (max-width: 991px) {
		padding: 0 80px;
	}
	@media (max-width: 767px) {
		padding: 0 32px;
	}
	@media (max-width: 575px) {
		padding: 0 20px;
	}
`;

const HomeFleetsBodyContainer = styled.div`
	position: absolute;
	height: 100%;
	background-image: url("/images/private-jet-travel/private-jet-travel-mask.png");
	background-position: center;
	background-size: cover;
	background-repeat: no-repeat;
	scroll-snap-align: start;
	width: 100vw;
	max-width: 100vw;
	display: flex;
	justify-content: center;
	align-items: center;
`;

const Overlay = styled.div`
	position: relative;
	width: 100vw;
	height: 100vh;
	background: rgba(249, 249, 249, 1);
`;

const EachSlide = styled.div`
	width: 100vw;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;
